import type { Provider } from '@angular/core';
import { isDevMode } from '@angular/core';
import { DatadogService } from '@cudirect/sawdust/datadog';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { LogLevel } from '@cudirect/sawdust/types';
import { Logger } from '@datadog/browser-logs';
import { DataDogBrowserLogsTransport } from '@loglayer/transport-datadog-browser-logs';
import { LOG_LAYER_CONFIG } from '@shared/loglayer/loglayer-config.token';
import { createLogLayer } from '@shared/loglayer/loglayer.module';
import { LOG_LAYER } from '@shared/loglayer/loglayer.token';
import { dependencies } from '@workflow/class/workflow.dependency';
import { environment } from 'environments/environment';
import { ConsoleTransport } from 'loglayer';
import { serializeError } from 'serialize-error';

const isDevLike = isDevMode() || environment.e2e;

export const provideSawDust = (serviceName: string, env: string, logLevel: LogLevel = LogLevel.info): Provider[] => [
  {
    provide: LOG_LAYER_CONFIG,
    useFactory: (datadogService: DatadogService) => {
      return {
        errorSerializer: serializeError,
        metadataFieldName: 'metadata',
        transport: [
          isDevLike || typeof window === 'undefined'
            ? new ConsoleTransport({
                logger: console,
                level: environment.logLevel as keyof typeof LogLevel
              })
            : new DataDogBrowserLogsTransport({
                logger: {
                  info: (message: string, messageContext?: object, error?: Error): void => {
                    datadogService.info(message, messageContext, error);
                  },
                  warn: (message: string, messageContext?: object, error?: Error): void => {
                    datadogService.info(message, messageContext, error);
                  },
                  error: (message: string, messageContext?: object, error?: Error): void => {
                    datadogService.info(message, messageContext, error);
                  },
                  debug: (message: string, messageContext?: object, error?: Error): void => {
                    datadogService.info(message, messageContext, error);
                  }
                } as Logger
              })
        ]
      };
    },
    deps: [DatadogService]
  },
  { provide: LOG_LAYER, useFactory: createLogLayer, deps: [LOG_LAYER_CONFIG] },
  {
    provide: SawDust,
    useFactory: (logLayer: ReturnType<typeof createLogLayer>, datadogService: DatadogService) => {
      const isServer = typeof window === 'undefined';
      if (isServer) {
        const sawdust = new SawDust();
        const consoleLayer = createLogLayer({
          errorSerializer: serializeError,
          metadataFieldName: 'metadata',
          transport: [
            new ConsoleTransport({
              logger: console,
              level: environment.logLevel as keyof typeof LogLevel
            })
          ]
        });
        sawdust.init({ serviceName, environment: env, logLevel, logLayer: consoleLayer });
        sawdust.withContext({ env, source: 'server' });
        dependencies.logger = sawdust;
        return sawdust;
      }

      const sawdust = new SawDust();
      sawdust.init({
        serviceName,
        environment: env,
        logLevel,
        logLayer,
        datadogService
      });
      sawdust.withContext({
        env,
        source: 'client'
      });
      dependencies.logger = sawdust;
      return sawdust;
    },
    deps: [LOG_LAYER, DatadogService]
  }
];
